/**
 * Serial number capture at checkout.
 *
 * Products configured for SERIAL tracking sell identifiable units, so the
 * cashier must name the exact one leaving stock. This modal collects one
 * serial per unit for every tracked line and validates each against live
 * inventory as it is entered — an unknown or already-sold serial is rejected
 * at the counter rather than at payment.
 *
 * Pay stays locked until every tracked line is complete. The server re-checks
 * each serial inside the completion transaction, so this is a usability gate,
 * not the source of truth.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input } from '@bbpos/ui';
import { GLASS, cn } from '@/constants/design-tokens';
import { productsService } from '@/services/products.service';

export interface SerialCaptureLine {
  /** Cart line id. */
  id: string;
  productId: string;
  name: string;
  sku: string;
  quantity: number;
  serials: string[];
}

export interface SerialCaptureModalProps {
  open: boolean;
  lines: SerialCaptureLine[];
  locationId?: string;
  /** Persist the scanned serials for one line. */
  onChange: (lineId: string, serials: string[]) => void;
  /** All lines satisfied — continue to payment. */
  onConfirm: () => void;
  onClose: () => void;
}

type EntryState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'error'; message: string };

export function SerialCaptureModal({
  open,
  lines,
  locationId,
  onChange,
  onConfirm,
  onClose,
}: SerialCaptureModalProps) {
  const [entry, setEntry] = useState<Record<string, string>>({});
  const [state, setState] = useState<Record<string, EntryState>>({});
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  // Reset transient entry state whenever the modal is (re)opened, so a
  // previous session's error text can't linger over a fresh scan.
  useEffect(() => {
    if (!open) return;
    setEntry({});
    setState({});
    const t = setTimeout(() => firstInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  const remainingFor = useCallback(
    (line: SerialCaptureLine) => line.quantity - line.serials.length,
    [],
  );

  const allComplete = useMemo(
    () => lines.every((l) => remainingFor(l) === 0),
    [lines, remainingFor],
  );

  const addSerial = useCallback(
    async (line: SerialCaptureLine) => {
      const raw = (entry[line.id] ?? '').trim();
      if (!raw) return;

      if (line.serials.length >= line.quantity) {
        setState((s) => ({
          ...s,
          [line.id]: { kind: 'error', message: `All ${line.quantity} unit(s) already have a serial.` },
        }));
        return;
      }
      // Case-insensitive: the same unit scanned twice is still the same unit.
      if (line.serials.some((s) => s.toUpperCase() === raw.toUpperCase())) {
        setState((s) => ({
          ...s,
          [line.id]: { kind: 'error', message: `"${raw}" is already on this line.` },
        }));
        return;
      }

      setState((s) => ({ ...s, [line.id]: { kind: 'checking' } }));
      try {
        const result = await productsService.lookupSerial(line.productId, raw, locationId);
        if (!result.ok) {
          setState((s) => ({
            ...s,
            [line.id]: { kind: 'error', message: result.message ?? 'That serial cannot be sold.' },
          }));
          return;
        }
        onChange(line.id, [...line.serials, result.serial_number ?? raw]);
        setEntry((e) => ({ ...e, [line.id]: '' }));
        setState((s) => ({ ...s, [line.id]: { kind: 'idle' } }));
      } catch {
        // Offline or server error — never silently accept an unverified unit.
        setState((s) => ({
          ...s,
          [line.id]: { kind: 'error', message: 'Could not verify the serial. Check the connection and retry.' },
        }));
      }
    },
    [entry, locationId, onChange],
  );

  const removeSerial = useCallback(
    (line: SerialCaptureLine, serial: string) => {
      onChange(line.id, line.serials.filter((s) => s !== serial));
    },
    [onChange],
  );

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="serial-capture-title"
    >
      <div className={cn(GLASS, 'w-full sm:w-[560px] max-h-[92vh] flex flex-col animate-slide-up')}>
        {/* ── Header ───────────────────────────────────────────────── */}
        <div className="px-5 py-4 flex items-start justify-between">
          <div>
            <h2
              id="serial-capture-title"
              className="text-lg font-semibold bg-gradient-to-r from-brand-600 to-indigo-600 bg-clip-text text-transparent"
            >
              Scan Serial Numbers
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              One serial per unit — checked against stock as you scan
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px]"
            aria-label="Close serial capture dialog"
          >
            ✕
          </Button>
        </div>

        {/* ── Lines ────────────────────────────────────────────────── */}
        <div className="px-5 pb-4 overflow-y-auto flex-1 space-y-4">
          {lines.map((line, idx) => {
            const remaining = remainingFor(line);
            const lineState = state[line.id] ?? { kind: 'idle' as const };
            const done = remaining === 0;

            return (
              <div
                key={line.id}
                className={cn(
                  'rounded-xl border p-3 transition-all duration-200',
                  done
                    ? 'border-success-200 bg-success-50/60 dark:border-success-800 dark:bg-success-900/20'
                    : 'border-neutral-200 bg-white/60 dark:border-neutral-700 dark:bg-neutral-800/40',
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                      {line.name}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{line.sku}</p>
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium rounded-full px-2 py-0.5 shrink-0',
                      done
                        ? 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300'
                        : 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
                    )}
                  >
                    {line.serials.length} / {line.quantity}
                  </span>
                </div>

                {/* Captured serials */}
                {line.serials.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5 mb-2">
                    {line.serials.map((s) => (
                      <li
                        key={s}
                        className="flex items-center gap-1 text-xs font-mono rounded bg-neutral-100 dark:bg-neutral-700 px-2 py-1"
                      >
                        {s}
                        <button
                          type="button"
                          onClick={() => removeSerial(line, s)}
                          aria-label={`Remove serial ${s}`}
                          className="text-neutral-400 hover:text-danger-500 transition-colors"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Entry */}
                {!done && (
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <Input
                        ref={idx === 0 ? firstInputRef : undefined}
                        value={entry[line.id] ?? ''}
                        onChange={(v) => {
                          setEntry((e) => ({ ...e, [line.id]: v }));
                          setState((s) => ({ ...s, [line.id]: { kind: 'idle' } }));
                        }}
                        onKeyDown={(e) => {
                          // Barcode scanners emit Enter — treat it as "add".
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void addSerial(line);
                          }
                        }}
                        placeholder={`Scan serial (${remaining} left)`}
                        aria-label={`Serial number for ${line.name}`}
                        error={lineState.kind === 'error' ? lineState.message : undefined}
                        loading={lineState.kind === 'checking'}
                      />
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => void addSerial(line)}
                      disabled={lineState.kind === 'checking' || !(entry[line.id] ?? '').trim()}
                      className="min-h-[44px]"
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Footer ───────────────────────────────────────────────── */}
        <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {allComplete
              ? 'All units accounted for.'
              : 'Every tracked unit needs a serial before payment.'}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={!allComplete}
              className="min-h-[44px]"
            >
              Continue to Payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
